import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import type { AdminEffectivePermissions } from "../../../api/adminConsole";
import {
  accessSourceSummary,
  buildPermissionAreas,
  filterAreas,
  permissionTotals,
  type PermissionArea,
  type PermissionRow,
} from "../../../lib/effectivePermissionsView";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * "What this user can do" on the staff user page: one sentence on where access comes from, totals,
 * then every catalog area as a card of View / Change actions with a tick (held), a dash (not held)
 * or a clock (granted by a role, applies after the switch to roles). MFA and critical risk are small
 * icons with tooltips; permission keys appear only when "Show permission keys" is on.
 */
export function EffectivePermissionsPanel({
  data,
}: {
  data: AdminEffectivePermissions;
}) {
  const [query, setQuery] = useState("");
  const [heldOnly, setHeldOnly] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const areas = useMemo(() => buildPermissionAreas(data), [data]);
  const totals = useMemo(() => permissionTotals(areas), [areas]);
  const shown = useMemo(
    () => filterAreas(areas, { query, heldOnly }),
    [areas, query, heldOnly],
  );
  const roleOnlyCount = areas
    .flatMap((a) => a.rows)
    .filter((r) => r.roleOnly).length;
  // MFA is enforced only once roles decide access (v2); under groups it is informational.
  const mfaNote =
    data.model === "v2"
      ? "Needs a recent MFA sign-in"
      : "Will need a recent MFA sign-in once roles decide access";

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {accessSourceSummary(data)}
        </Typography>
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Can do ${totals.held} of ${totals.total}`}
          />
          {totals.critical > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="error"
              icon={<WarningAmberRoundedIcon />}
              label={`${totals.critical} high-impact`}
            />
          )}
          {totals.needMfa > 0 && (
            <Chip
              size="small"
              variant="outlined"
              icon={<LockRoundedIcon />}
              label={`${totals.needMfa} need MFA`}
            />
          )}
        </Stack>
      </Box>

      {data.model === "v1" && roleOnlyCount > 0 && (
        <Alert severity="info" icon={<ScheduleRoundedIcon />}>
          {roleOnlyCount} permission{roleOnlyCount === 1 ? "" : "s"} marked with
          a clock come from roles. Roles take effect when access switches from
          groups to roles; today groups decide.
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        alignItems={{ sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search permissions"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: { sm: 260 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={heldOnly}
              onChange={(e) => setHeldOnly(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Only what they can do</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showKeys}
              onChange={(e) => setShowKeys(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Show permission keys</Typography>}
        />
      </Stack>

      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No permissions match.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {shown.map((area) => (
            <AreaCard
              key={area.name}
              area={area}
              total={areas.find((a) => a.name === area.name)!}
              showKeys={showKeys}
              mfaNote={mfaNote}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

function AreaCard({
  area,
  total,
  showKeys,
  mfaNote,
}: {
  area: PermissionArea;
  total: PermissionArea;
  showKeys: boolean;
  mfaNote: string;
}) {
  const theme = useTheme();
  const all = total.heldCount === total.rows.length;
  const none = total.heldCount === 0;
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        bgcolor: none
          ? alpha(theme.palette.text.primary, 0.02)
          : "background.paper",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">{area.name}</Typography>
        <Chip
          size="small"
          label={
            all
              ? "Full access"
              : none
                ? "No access"
                : `${total.heldCount} of ${total.rows.length}`
          }
          color={all ? "success" : "default"}
          variant={none ? "outlined" : "filled"}
        />
      </Stack>
      <Stack spacing={0.75}>
        {area.rows.map((row) => (
          <PermissionLine
            key={row.key}
            row={row}
            showKey={showKeys}
            mfaNote={mfaNote}
          />
        ))}
      </Stack>
    </Box>
  );
}

function PermissionLine({
  row,
  showKey,
  mfaNote,
}: {
  row: PermissionRow;
  showKey: boolean;
  mfaNote: string;
}) {
  const status = row.held ? (
    <Tooltip title="Can do this">
      <CheckCircleRoundedIcon
        color="success"
        sx={{ fontSize: 18 }}
        aria-label="Allowed"
      />
    </Tooltip>
  ) : row.roleOnly ? (
    <Tooltip title="Granted by a role; applies after the switch to roles">
      <ScheduleRoundedIcon
        color="info"
        sx={{ fontSize: 18 }}
        aria-label="After switch to roles"
      />
    </Tooltip>
  ) : (
    <Tooltip title="Cannot do this">
      <RemoveCircleOutlineRoundedIcon
        sx={{ fontSize: 18, color: "text.disabled" }}
        aria-label="Not allowed"
      />
    </Tooltip>
  );
  return (
    <Stack direction="row" gap={1} alignItems="flex-start">
      <Box sx={{ pt: "1px" }}>{status}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          gap={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            label={row.kind === "view" ? "View" : "Change"}
            variant="outlined"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: row.held ? "text.primary" : "text.secondary" }}
          >
            {row.label}
          </Typography>
          {row.risk === "critical" && (
            <Tooltip title="High-impact action">
              <WarningAmberRoundedIcon
                color="error"
                sx={{ fontSize: 16 }}
                aria-label="High-impact"
              />
            </Tooltip>
          )}
          {row.requiresMfa && (
            <Tooltip title={mfaNote}>
              <LockRoundedIcon
                sx={{ fontSize: 15, color: "text.secondary" }}
                aria-label="Needs MFA"
              />
            </Tooltip>
          )}
        </Stack>
        {(row.sources.length > 0 || showKey) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", overflowWrap: "anywhere" }}
          >
            {row.sources.length > 0 && <>from {row.sources.join(", ")}</>}
            {row.sources.length > 0 && showKey && " · "}
            {showKey && (
              <Box component="span" sx={{ fontFamily: MONO }}>
                {row.key}
              </Box>
            )}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
